import React, { useState, useEffect, useRef } from 'react'
import { Pagination, Dropdown, Menu, Button, Row, Tag, Col, Spin } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { PaginationProps } from 'antd/lib/pagination'
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown'
import { faUser } from '@fortawesome/pro-solid-svg-icons/faUser'
import { faUserPlus } from '@fortawesome/pro-solid-svg-icons/faUserPlus'
import { faCheckSquare } from '@fortawesome/pro-solid-svg-icons/faCheckSquare'
import { faTimesCircle } from '@fortawesome/pro-solid-svg-icons/faTimesCircle'
import { faEyeSlash } from '@fortawesome/pro-solid-svg-icons/faEyeSlash'
import { faEye } from '@fortawesome/pro-solid-svg-icons/faEye'
import getContent from './components'
import { generateColumns } from '../../helpers/generateTableColumn'
import styles from './index.module.scss'
import translationsTemplate from '../../constants/translations'
import { isObject, mergeDeep } from '../../helpers/mergeDeep'
import Loader from './components/Loader'
import { IRankmiTable, ITableHeaders } from 'interfaces/tableProps'
import { IFilterData, IFilterOption } from 'interfaces/filterProps'
import { AreasSelector } from '../../components/AreasSelector'
import { Filters } from '../filters'
import { ActiveFilterViewer } from '../active-filter-viewer'
import Actions from './components/Actions'
import FastActions from './components/FastActions'
import ShowColumns from './components/ShowColumn/ShowColumns'
import { es_dateFormat } from 'constants/date'
import CustomSearch from './components/Search/Search'
import Table from './components/Table/Table'
import useWindowDimensions from 'helpers/useWindowDimensions'
import { getImmediateLocale } from 'helpers/locale.helpers'
import { gridBreakpointsEnum } from 'enum/breakpoints'
import Summary from 'modules/Summary'
import QuickFilters from 'modules/quick-filters'
import * as helpers from './index.helper'

export const RankmiTable: React.FC<IRankmiTable> = ({
  // Data
  columnHeaders = [],
  filtersColumns = [],
  data = [],
  total = 0,
  customTotalSelected = undefined,
  areas = [],
  search = undefined,
  openFilters = undefined,
  openAreas = undefined,
  onFiltersVisibilityChange = undefined,
  onAreasFiltersVisibilityChange = undefined,
  selectedArea = undefined,
  mandatoryAreas = false,
  actions = [],
  fastActions = [],
  selectedRows = undefined,
  currentPage = undefined,
  initialRowsPerPage = 10,
  selectedFilters = undefined,
  showFooter = true,
  // Handlers
  onFastActionSelect = undefined,
  simpleSelectAll = false,
  onAllRowKeys = undefined,
  onSortChange = [] as any,
  onAreaSelected = undefined,
  onAddClick = undefined,
  onSearch = undefined,
  onRowSelected = undefined,
  quickFilters = undefined,
  summary = undefined,
  onFilterChange = undefined,
  onPaginationChange = undefined,
  onRowsPerPageChange = undefined,
  onColumnVisibilityChange = undefined,
  // Events
  filtersLoading = false,
  areasFiltersLoading = false,
  // Others
  onlySimpleFilters = false,
  responsive = false,
  showScrollButtons = false,
  changeColumnsVisibility = false,
  scroll = undefined,
  loading = false,
  locale = {},
  dateFormat = es_dateFormat,
  countIcon = faUser,
  addButtonIcon = faUserPlus,
  unSelectedAllRows,
  delaySearch = false,
  type = 0,
  customEmpty,
  replaceTableComponent,
  actionElements = [],
  clearFilterOnConditionChange = true,
  rowSelectionType = 'checkbox',
  customTrigger = false,
  fixedFilters = undefined,
  filtersClassName = '',
  overlayActionsClassName = '',
  customColumnVisibilityNode = undefined,
}) => {
  const [columns, setColumns] = useState([] as any)
  const [hasSensitiveData, setHasSensitiveData] = useState(false)
  const [showSensitiveData, setShowSensitiveData] = useState(false)
  const [dataSource, setDataSource] = useState([] as any)
  const [page, setPage] = useState(1)
  const [openRowsPerPageMenu, setOpenRowsPerPageMenu] = useState(false)
  const rowsPerPageArray = [10, 20, 30, 50, 100]
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageArray[0])
  const [selectedRowsKeys, setSelectedRowsKeys] = useState([] as any)
  const [unselectedRowsKeys, setUnselectedRowsKeys] = useState([] as number[])
  const [translations, setTranslations] = useState(getImmediateLocale(locale, translationsTemplate))
  const [activeFilters, setActiveFilters] = useState<IFilterData>({})
  const [filterColumns, setFilterColumns] = useState<IFilterOption[]>([])
  const [showFiltersModal, setShowFiltersModal] = useState(false)
  const [showAreasModal, setShowAreasModal] = useState(false)
  const [fastOptionSelected, setFastOptionSelected] = useState(0 as any)
  const [allSelected, setAllSelected] = useState(false)
  const [visibleColumns, setVisibleColumns] = useState<ITableHeaders[]>()
  const dataRef = useRef(data)
  const totalRef = useRef(0)

  useEffect(() => {
    if (unSelectedAllRows) unSelectedAllRows(() => handleUnselectAllRows())
  }, [])

  useEffect(() => {
    openFilters !== undefined && setShowFiltersModal(openFilters)
  }, [openFilters])

  useEffect(() => {
    openAreas !== undefined && setShowAreasModal(openAreas)
  }, [openAreas])

  useEffect(() => {
    isObject(locale) && Object.keys(locale).length > 0 && setTranslations(mergeDeep(locale, translationsTemplate))
  }, [locale])

  useEffect(() => {
    rowsPerPage !== initialRowsPerPage && setRowsPerPage(initialRowsPerPage)
  }, [initialRowsPerPage])

  useEffect(() => {
    if (Array.isArray(selectedRows) && !allSelected) {
      const isEqual = selectedRows.length === selectedRowsKeys.length && selectedRows.every((row: number, index) => row === selectedRowsKeys[index])
      !isEqual && setSelectedRowsKeys(selectedRows)
    }
  }, [selectedRows, selectedRowsKeys])

  useEffect(() => {
    currentPage && currentPage !== page && setPage(currentPage)
  }, [currentPage])

  useEffect(() => {
    if (selectedFilters) {
      setActiveFilters(selectedFilters)
      selectedFilters.data?.length && handleUnselectAllRows('handleUnselectAllRowsOnFilter')
    }
  }, [selectedFilters])

  useEffect(() => {
    totalRef.current = total
  }, [total])

  useEffect(() => {
    selectedArea && selectedArea.parentId && handleUnselectAllRows('handleUnselectAllRowsOnFilter')
  }, [selectedArea])

  const handleFakeSelected = (dataRef: any[], currentUnselectedRowsKeys = unselectedRowsKeys) => {
    let aux = [] as any[]
    let count = currentUnselectedRowsKeys.length

    dataRef
      .filter((row: any) => !row.checkboxDisabled)
      .map(({ key }: any) => {
        if (!currentUnselectedRowsKeys.includes(key)) {
          aux.push(key)
          count++
        }
      })
    count = totalRef.current - count
    aux = aux.concat(new Array(count >= 0 ? count : 0))
    setSelectedRowsKeys(aux)
    onRowSelected && onRowSelected({ selectedRowKeys: aux, selectedRows: [], allSelected: true, unselectedRowKeys: currentUnselectedRowsKeys, dispatcher: 'handleFakeSelected' })
    setAllSelected(true)
  }

  const handleSelectAllInit = (dataRef: any[]) => {
    setUnselectedRowsKeys([])
    handleFakeSelected(dataRef, [])
  }

  const checkboxOptions = (
    <Dropdown
      overlay={
        <Menu>
          {(!!onAllRowKeys || simpleSelectAll) && (
            <Menu.Item>
              <a
                onClick={() => {
                  onAllRowKeys ? onAllRowKeys() : handleSelectAllInit(dataRef.current)
                }}
              >
                {translations.header.selectAll}
              </a>
            </Menu.Item>
          )}
          <Menu.Item>
            <a onClick={() => handleUnselectAllRows()}>{translations.header.clearSelection}</a>
          </Menu.Item>
        </Menu>
      }
      overlayClassName="rankmiTable-checkboxOptions"
      placement="bottomLeft"
    >
      <a>
        <FontAwesomeIcon icon={faChevronDown} />
      </a>
    </Dropdown>
  )

  const createFiltersColumns = () => {
    if (filtersColumns.length) return filtersColumns
    return columnHeaders.filter(({ filterable }) => filterable !== false)
  }

  useEffect(() => {
    setHasSensitiveData(false)
    const auxColumns = columnHeaders.filter(({ visible }) => visible !== false)
    const columns = auxColumns.map(({ title, key, variant, sorteable, render, align, width, fixed, className, visible, type, hideable, sensitive }: any) => {
      if (sensitive) setHasSensitiveData(true)
      return generateColumns(title, key, getContent(variant, render, dateFormat, sensitive), sorteable, align, width, fixed, className, visible, type, hideable)
    })
    setVisibleColumns(helpers.getMatchVisibleColumns(columns.slice() as ITableHeaders[], visibleColumns ?? []))
    !!onRowSelected &&
      columns.unshift({
        title: checkboxOptions as any,
        render: '',
        width: '10px',
        className: 'rankmiTable-checkBoxOptions',
        fixed: columns.findIndex(({ fixed }: any) => fixed === 'left') >= 0 && 'left',
        align: 'left',
        dataIndex: 'checkoptions',
        key: 'checkoptions',
        sorter: undefined,
        visible: 'visible',
        type: 'string',
        hideable: true,
      })
    setColumns(helpers.getVisibleColumns(helpers.getMatchVisibleColumns(columns as ITableHeaders[], visibleColumns ?? [])))
    const filterColumns = createFiltersColumns()
    setFilterColumns(
      filterColumns.map(
        ({
          title,
          value,
          key,
          type = 'string',
          list,
          filterOptions,
          onSearch,
          excludedOperators,
          min,
          max,
          predicate = undefined,
          optionalId = undefined,
          searchStringKey = undefined,
          childrensGroup = undefined,
          async = undefined,
          additionalFields = undefined,
        }) => ({
          label: value || title,
          value: key,
          predicate,
          optionalId,
          type,
          list,
          min,
          max,
          filterOptions,
          onSearch,
          excludedOperators,
          searchStringKey,
          childrensGroup,
          async,
          additionalFields,
        })
      ) as IFilterOption[]
    )
  }, [columnHeaders, translations])

  useEffect(() => {
    setDataSource(data)
    dataRef.current = data
    if (allSelected) handleFakeSelected(dataRef.current)
  }, [data])

  const handleChangeRowsPerPage = (current: number) => {
    setRowsPerPage(current)
    const pages = Math.ceil(total / current)
    if (pages < page) {
      setPage(pages)
      onPaginationChange && onPaginationChange(pages)
    }
    onRowsPerPageChange && onRowsPerPageChange(current)
  }

  const handleChangeCurrentPage = (current: number) => {
    setPage(current)
    onPaginationChange && onPaginationChange(current)
  }

  const handleFiltersChange = (activeFilters: IFilterData) => {
    setActiveFilters(activeFilters)
    onFilterChange(activeFilters)
    activeFilters.data?.length && handleUnselectAllRows('handleUnselectAllRowsOnFilter')
  }

  const handleFiltersVisibilityChange = (state: boolean) => {
    setShowFiltersModal(state)
    onFiltersVisibilityChange && onFiltersVisibilityChange(state)
  }

  const handleFastActionSelect = (option: any) => {
    setFastOptionSelected(option)
    onFastActionSelect && onFastActionSelect(option)
  }

  const handleSetVisibility = (columns: any[]) => {
    setVisibleColumns(columns.slice())
    setColumns(helpers.getVisibleColumns(columns.slice()))
    !!onRowSelected &&
      columns.unshift({
        title: checkboxOptions as any,
        render: '',
        width: '10px',
        className: 'rankmiTable-checkBoxOptions',
        fixed: columns.findIndex(({ fixed }: any) => fixed === 'left') >= 0 && 'left',
        align: 'left',
        dataIndex: 'checkoptions',
        key: 'checkoptions',
        sorter: undefined,
        visible: 'visible',
        type: 'string',
        hideable: true,
      })
    onColumnVisibilityChange && onColumnVisibilityChange(columns)
  }

  const handleSearch = async (text: string) => {
    onSearch(text)
    text && handleUnselectAllRows('handleUnselectAllRowsOnFilter')
  }

  const handleAreaSelected = (data: any) => {
    onAreaSelected && onAreaSelected(data)
    data.parentId && handleUnselectAllRows('handleUnselectAllRowsOnFilter')
  }

  const handleUnselectAllRows = (dispatcher = 'handleUnselectAllRows') => {
    setSelectedRowsKeys([])
    setAllSelected(false)
    setUnselectedRowsKeys([])
    onRowSelected && onRowSelected({ selectedRowKeys: [], selectedRows: [], allSelected: false, unselectedRowKeys: [], dispatcher })
  }

  const { width } = useWindowDimensions()
  const isMobile = width < gridBreakpointsEnum.mediumDevicesUp
  const itemRender: PaginationProps['itemRender'] = (_, type, originalElement) => {
    if (isMobile) {
      if (type === 'prev') {
        return <a className={`${styles.paginationButton}`}>{translations.controls.PaginationPrev}</a>
      }
      if (type === 'next') {
        return <a className={`${styles.paginationButton}`}>{translations.controls.PaginationNext}</a>
      }
      return originalElement
    }

    return originalElement
  }

  const cardColor = ['#f5f5f5', '#d7f5e9', '#fff9e3', '#fee0e0', '#f5f5f5', '#d7f5e9']
  const color = ['rgb(36 65 108)', 'rgb(57, 112, 54)', 'rgb(141 92 8)', 'rgb(171 20 33)', 'rgb(36 65 108)', 'rgb(57, 112, 54)']

  let summaryData
  let summaryType: string = 'default'
  let percentage = false
  let summaryTotal = 1
  let textAlign = 'left'
  let title = ''

  if (summary) {
    summaryType = summary?.type ?? 'default'
    percentage = summary?.percentage ?? false
    summaryTotal = summary?.total ?? 1
    textAlign = summary?.textAlign ?? 'left'
    title = summary?.title ?? ''

    const { data: currentData } = summary

    if (currentData) {
      const areAllObjects = Object.values(currentData).every((val) => typeof val === 'object' && val !== null)

      let parseData = { ...currentData }

      if (!areAllObjects) {
        parseData = Object.keys(currentData).reduce((accumulator: any, key) => {
          accumulator[key] = { value: currentData[key] }
          return accumulator
        }, {})
      }

      summaryData = Object.entries(parseData).map(([key, val]: [any, any], index) => ({
        id: index + 1,
        key: val.key ?? key,
        value: val?.value,
        cardColor: summaryType === 'stoplight' && !val?.cardColor ? cardColor[index] : val?.cardColor ?? 'white',
        percentage: percentage ? (val.value / summaryTotal) * 100 : undefined,
        divider: val.divider ?? '',
        total: summaryTotal,
        prefix: val.prefix ?? '',
        color: summaryType === 'stoplight' && !val?.color ? color[index] : val?.color ?? 'rgb(36, 65, 108)',
      }))
    }
  }

  const summaryProps = {
    data: summaryData,
    percentage: percentage,
    total: summaryTotal,
    type: summaryType,
    title,
  }

  return (
    <div className={`rankmiTable-container ${styles.rankmiTableContainer} ${!showSensitiveData ? styles.hideSensitiveData : ''}`}>
      {quickFilters && <QuickFilters callback={quickFilters?.onQuickFilterChange} fields={quickFilters?.fields} validate={quickFilters?.validate} />}
      {!!summary && <Summary align={textAlign} {...summaryProps} />}
      <Row className={styles.firstRow} justify="end">
        <Col className="mb-2 d-flex" lg={{ order: 1, span: 12 }} md={{ order: 1, span: 12 }} sm={{ order: 3, span: 24 }} xs={{ order: 3, span: 24 }}>
          {!!onAreaSelected && (
            <Col lg={8} md={8} sm={24} xs={24}>
              <div className={styles.areasSelectorContainer}>
                <div className={styles.label}>{translations.modal.areas.label}</div>
                <AreasSelector
                  areas={areas}
                  loading={areasFiltersLoading || loading}
                  locale={translations.modal.areas}
                  mandatoryArea={mandatoryAreas}
                  open={showAreasModal}
                  selectedArea={selectedArea}
                  onAreaSelected={handleAreaSelected}
                  onSetShowAreasModal={onAreasFiltersVisibilityChange}
                />
              </div>
            </Col>
          )}
          <Col className="align-self-end" lg={16} md={16} sm={0} xs={0}>
            {!isMobile && onFilterChange && (
              <div className={styles.filterButtonContainer}>
                <Filters
                  className={filtersClassName}
                  clearOnChangeCondition={clearFilterOnConditionChange}
                  columns={filterColumns}
                  customTrigger={customTrigger}
                  dateFormat={dateFormat}
                  loading={filtersLoading || loading}
                  locale={translations.filters}
                  onlySimpleFilters={onlySimpleFilters}
                  open={showFiltersModal}
                  selectedFilters={activeFilters}
                  onChange={handleFiltersChange}
                  onVisibilityChange={handleFiltersVisibilityChange}
                />
              </div>
            )}
          </Col>
        </Col>
        <Col
          className={`d-flex mb-2 justify-end ${styles.w100} ${styles.countingContainer}`}
          lg={{ order: 2, span: 12 }}
          md={{ order: 2, span: 12 }}
          sm={{ order: 2, span: !!onAreaSelected ? 24 : 12 }}
          xs={{ order: 2, span: !!onAreaSelected ? 24 : 12 }}
        >
          <Col className="d-flex align-center justify-end mb-2">
            {actionElements.length > 0 && (type === 0 || type === 1) && <div className={styles.actionsContainer}>{actionElements}</div>}
            {!!onAddClick && type === 0 && (
              <Button
                className={`rankmiTable-addButton mr-2 ${styles.addButton} ${!!actions.length && (type === 0 || type === 1) && onFilterChange && styles.dSpan}`}
                type="primary"
                onClick={() => onAddClick && onAddClick()}
              >
                <FontAwesomeIcon icon={addButtonIcon} />
                {translations.header.addButton}
              </Button>
            )}
          </Col>
          {!!actions.length && type !== 2 && (
            <Col className={`d-flex align-center justify-end mb-2 ${styles.w100}`}>
              <Actions actions={actions} overlayClassName={overlayActionsClassName} translations={translations} />
            </Col>
          )}
          {isMobile && onFilterChange && (
            <Col className="w-100" lg={0} md={0}>
              <div className={`${styles.filterButtonContainer}`}>
                <Filters
                  clearOnChangeCondition={clearFilterOnConditionChange}
                  columns={filterColumns}
                  customTrigger={customTrigger}
                  dateFormat={dateFormat}
                  loading={filtersLoading || loading}
                  locale={translations.filters}
                  onlySimpleFilters={onlySimpleFilters}
                  open={showFiltersModal}
                  selectedFilters={activeFilters}
                  onChange={handleFiltersChange}
                  onVisibilityChange={handleFiltersVisibilityChange}
                />
              </div>
            </Col>
          )}
        </Col>
        <Col className="mb-2 d-flex justify-end" lg={{ order: 4, span: 12 }} md={{ order: 4, span: 12 }} sm={{ order: 1, span: 24 }} xs={{ order: 1, span: 24 }}>
          <div className={`d-flex align-center justify-between mb-2 ${styles.w100}`}>
            <div className="d-flex w-100">
              {!!onAddClick && type === 1 && (
                <Button className={`rankmiTable-addButton ${styles.addButton} mr-2`} type="primary" onClick={() => onAddClick && onAddClick()}>
                  <FontAwesomeIcon icon={addButtonIcon} />
                  {translations.header.addButton}
                </Button>
              )}

              {!!onSearch && <CustomSearch delaySearch={delaySearch} translations={translations} value={search} onChange={(text: string) => handleSearch(text)} />}
              {!!hasSensitiveData && (
                <Button className={styles.sensitive} size="small" onClick={() => setShowSensitiveData(!showSensitiveData)}>
                  <FontAwesomeIcon icon={showSensitiveData ? faEyeSlash : faEye} />
                </Button>
              )}
              {!!actions.length && type === 2 && <Actions actions={actions} overlayClassName={overlayActionsClassName} translations={translations} onSearch={!!onSearch} />}
              {!!fastActions.length && <FastActions fastActions={fastActions} fastActionSelected={fastOptionSelected} translations={translations} onFastActionSelected={handleFastActionSelect} />}
              {!!changeColumnsVisibility && !!visibleColumns?.length && (
                <ShowColumns columns={visibleColumns} customColumnVisibilityNode={customColumnVisibilityNode} translations={translations} onChange={handleSetVisibility} />
              )}
            </div>
          </div>
        </Col>
        <Col className="mb-2 d-flex" lg={{ order: 3, span: 12 }} md={{ order: 3, span: 12 }} sm={{ order: 4, span: 24 }} xs={{ order: 4, span: 24 }}>
          <div className="d-flex align-center justify-between mb-2 ">
            <div className="rankmiTable-rowsCount d-flex">
              <Tag className={styles.rowsCount}>
                <FontAwesomeIcon className="mr-1" icon={countIcon} /> {(total || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}{' '}
                {translations.header[total !== 1 ? 'rowsCount_plural' : 'rowsCount']}
              </Tag>
              {!!selectedRowsKeys.length && (
                <Tag className={styles.rowsRowsCount}>
                  <div className="w-100 h-100 d-flex justify-between align-center">
                    <FontAwesomeIcon className="mr-1" icon={faCheckSquare} />
                    {(customTotalSelected || selectedRowsKeys.length || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}{' '}
                    {translations.header[(customTotalSelected || selectedRowsKeys.length) !== 1 ? 'selectedRows_plural' : 'selectedRows']}
                    <FontAwesomeIcon className="ml-1 mr-0" icon={faTimesCircle} onClick={() => handleUnselectAllRows()} />
                  </div>
                </Tag>
              )}
            </div>
          </div>
        </Col>
        <Col className="mb-2 d-flex" lg={{ order: 4, span: 24 }} md={{ order: 4, span: 24 }} sm={{ order: 5, span: 24 }} xs={{ order: 5, span: 24 }}>
          <ActiveFilterViewer
            activeFilters={activeFilters}
            columns={filterColumns}
            fixedFilters={fixedFilters}
            locale={translations.filters}
            openFilters={() => handleFiltersVisibilityChange(true)}
            onChange={handleFiltersChange}
          />
        </Col>
      </Row>
      <Spin indicator={<Loader />} spinning={loading}>
        <Table
          allSelected={allSelected}
          columns={columns}
          customEmpty={customEmpty}
          dataSource={dataSource}
          emptyListMessage={translations.table?.emptyMessage || ''}
          loading={loading}
          replaceTableComponent={replaceTableComponent}
          responsive={responsive}
          rowSelectionType={rowSelectionType}
          scroll={scroll}
          selectedRowsKeys={selectedRowsKeys}
          setSelectedRowsKeys={setSelectedRowsKeys}
          setUnselectedRowsKeys={setUnselectedRowsKeys}
          showScrollButtons={showScrollButtons}
          unselectedRowsKeys={unselectedRowsKeys}
          onRowSelected={onRowSelected}
          onSortChange={onSortChange}
        />
      </Spin>
      {showFooter && (onRowsPerPageChange || onPaginationChange) && (
        <Col className={styles.paginationControls}>
          {onRowsPerPageChange && (
            <Col className={`d-flex rankmiTable-rowsPerPage ${styles.containerRowsPerPage}`}>
              <span className={`${styles.dropdownText}`}>{translations.controls.rowsPerPage}:</span>
              <Dropdown
                className={styles.rowsPerPage}
                overlay={
                  <Menu>
                    {rowsPerPageArray.map((rows, index) => (
                      <Menu.Item key={`rowsPerPageOpcion-${index}`}>
                        <a
                          onClick={() => {
                            handleChangeRowsPerPage(rows)
                            setOpenRowsPerPageMenu(false)
                          }}
                        >
                          {rows}
                        </a>
                      </Menu.Item>
                    ))}
                  </Menu>
                }
                overlayClassName={`rankmiTable-rowsPerPageDropdown ${styles.dropdown}`}
                placement="topLeft"
                visible={openRowsPerPageMenu}
                onVisibleChange={(visible: boolean) => setOpenRowsPerPageMenu(visible)}
              >
                <Button>
                  {rowsPerPage}
                  <FontAwesomeIcon icon={faChevronDown} style={{ marginLeft: '5px !important' }} />
                </Button>
              </Dropdown>
            </Col>
          )}
          {onPaginationChange && (
            <Col className={`rankmiTable-pagination ${styles.paginationContainer}`}>
              <Pagination
                className={styles.pagination}
                current={page}
                defaultCurrent={1}
                itemRender={itemRender}
                pageSize={rowsPerPage}
                simple={isMobile}
                size="small"
                total={total}
                onChange={(page: number) => handleChangeCurrentPage(page)}
              />
            </Col>
          )}
        </Col>
      )}
    </div>
  )
}
